/**
 * Project hub → "IA del proyecto" tab (PR-4 builder reorg, admin-only).
 *
 * Summary card with one row per project-level AI asset (context notes
 * + the three project-training kinds); each row's "Gestionar" swaps to
 * a state-local management sub-view with a "← IA del proyecto" back
 * link:
 *
 *   - Notes manager — filters + table + create/edit modal + archive +
 *     "regenerate affected snapshots" (preview count + live progress;
 *     PROXIED to the portal — 502/503 surfaced verbatim).
 *   - Training manager — one shared list/add/edit/delete component
 *     parameterized by `training_type` (column_docs | synonyms |
 *     example) against the `/projects/{slug}/training` endpoints.
 *
 * Note scope: `ai_context_notes` rows are global; this pane only shows
 * (and offers) the notes that reference THIS project's reports.
 */

import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  Select,
  type SelectOption,
} from "@omnitok/ui";
import {
  AlertCircle,
  Archive,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  EyeOff,
  HelpCircle,
  Pencil,
  Plus,
  RotateCcw,
  StickyNote,
  Tags,
  Trash2,
  X,
} from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import {
  DataTable,
  EmptyState,
  RowActions,
  type Column,
  type RowAction,
} from "@pervasivemind/admin-ui";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import { useT } from "@/i18n";
import {
  aiContextNotesAdminApi,
  formatAdminAiError,
  projectTrainingApi,
  truncateText,
  type AIContextNote,
  type AIContextNoteInput,
  type NoteRegenStatusResponse,
  type ProjectTrainingRow,
  type ProjectTrainingType,
} from "@/lib/admin-ai-api";
import { resolveLocalized } from "@pervasivemind/dashboard-render";
import type { DisplayLabel, Project, ReportDraft } from "@/types";

const REGEN_POLL_INTERVAL_MS = 2000;

/** The notes endpoint refuses anything above this (`Query(le=500)`), so
 *  it is the most this screen can ever see — not a display preference.
 *  When a response comes back exactly this long the list is CUT, and
 *  every count derived from it is a floor. Said out loud rather than
 *  rendered as a number that looks exact. */
const NOTES_HARD_LIMIT = 500;

type IAView = "summary" | "notes" | ProjectTrainingType;

const IA_VIEWS: readonly IAView[] = [
  "summary",
  "notes",
  "column_docs",
  "synonyms",
  "example",
];

/** An unknown or absent `?ia=` is the summary. Exported for the test —
 *  a hand-typed URL must not blank the tab. */
export function resolveIAView(raw: string | null): IAView {
  return IA_VIEWS.includes(raw as IAView) ? (raw as IAView) : "summary";
}

interface ReportPill {
  key: string;
  label: string;
}

interface ProjectIAPaneProps {
  project: Project;
  /** Drafts already scoped to this project by the hub. */
  drafts: ReportDraft[];
}

export function ProjectIAPane({
  project,
  drafts,
}: ProjectIAPaneProps): JSX.Element {
  const { t, locale } = useT();
  // The hub already keeps its tab in the URL; this keeps its sub-view
  // there too. Before, Back left the tab, a reload dropped you on the
  // summary, and "the notes of this project" had no link.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = resolveIAView(searchParams.get("ia"));
  const setView = useCallback(
    (next: IAView): void => {
      const params = new URLSearchParams(searchParams);
      if (next === "summary") params.delete("ia");
      else params.set("ia", next);
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const reportPills: ReportPill[] = useMemo(
    () =>
      drafts
        .map((d) => ({
          key: d.key,
          label:
            resolveLocalized(
              d.report_config.display_name as DisplayLabel | undefined,
              locale,
            ) || d.key,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [drafts, locale],
  );
  const reportKeys = useMemo(
    () => reportPills.map((p) => p.key),
    [reportPills],
  );

  if (view === "summary") {
    return (
      <IASummary
        project={project}
        reportKeys={reportKeys}
        onOpen={setView}
      />
    );
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => setView("summary")}
        className="inline-flex items-center gap-1 text-xs font-medium text-neutral-600 hover:text-neutral-900"
      >
        <ArrowLeft size={12} />
        {t("projectIA.back")}
      </button>
      {view === "notes" ? (
        <NotesManager
          reportKeys={reportKeys}
          reportPills={reportPills}
        />
      ) : (
        <TrainingManager slug={project.slug} type={view} />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// Summary — counts + "Gestionar" per asset kind
// ───────────────────────────────────────────────────────────

interface IASummaryProps {
  project: Project;
  reportKeys: string[];
  onOpen: (view: IAView) => void;
}

function IASummary({
  project,
  reportKeys,
  onOpen,
}: IASummaryProps): JSX.Element {
  const { t } = useT();

  const [notesCount, setNotesCount] = useState<number | null>(null);
  // True when the endpoint returned its maximum, i.e. the count below
  // is a floor and not a total.
  const [notesTruncated, setNotesTruncated] = useState(false);
  const [trainingCounts, setTrainingCounts] = useState<Record<
    ProjectTrainingType,
    number
  > | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [notes, training] = await Promise.all([
        aiContextNotesAdminApi.list({ limit: NOTES_HARD_LIMIT }),
        projectTrainingApi.list(project.slug),
      ]);
      setNotesTruncated(notes.length >= NOTES_HARD_LIMIT);
      setNotesCount(
        notes.filter((n) =>
          n.report_keys.some((k) => reportKeys.includes(k)),
        ).length,
      );
      const counts: Record<ProjectTrainingType, number> = {
        column_docs: 0,
        synonyms: 0,
        example: 0,
      };
      for (const row of training) {
        if (row.training_type in counts) counts[row.training_type] += 1;
      }
      setTrainingCounts(counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [project.slug, reportKeys, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Card variant="bordered" noPadding>
        <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-neutral-500">
          <Spinner size={16} ariaLabel={t("common.loading")} />
          {t("common.loading")}
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="bordered" className="border-error/40 bg-error/5">
        <CardBody>
          <div className="flex items-start gap-3 text-sm text-error-dark">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">{error}</p>
              <Button
                variant="danger"
                ghost
                size="xs"
                onClick={() => void load()}
                className="mt-3"
              >
                {t("common.retry")}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  const rows: Array<{
    view: IAView;
    icon: JSX.Element;
    title: string;
    description: string;
    count: string;
    countTitle?: string;
  }> = [
    {
      view: "notes",
      icon: <StickyNote size={16} />,
      title: t("projectIA.row.notes"),
      description: t("projectIA.row.notesDesc"),
      count: notesTruncated
        ? t("projectIA.activeCountAtLeast", { count: notesCount ?? 0 })
        : t("projectIA.activeCount", { count: notesCount ?? 0 }),
      countTitle: notesTruncated ? t("projectIA.truncatedHint") : undefined,
    },
    {
      view: "column_docs",
      icon: <BookOpen size={16} />,
      title: t("projectIA.row.column_docs"),
      description: t("projectIA.row.column_docsDesc"),
      count: t("projectIA.rowCount", {
        count: trainingCounts?.column_docs ?? 0,
      }),
    },
    {
      view: "synonyms",
      icon: <Tags size={16} />,
      title: t("projectIA.row.synonyms"),
      description: t("projectIA.row.synonymsDesc"),
      count: t("projectIA.rowCount", {
        count: trainingCounts?.synonyms ?? 0,
      }),
    },
    {
      view: "example",
      icon: <HelpCircle size={16} />,
      title: t("projectIA.row.example"),
      description: t("projectIA.row.exampleDesc"),
      count: t("projectIA.rowCount", {
        count: trainingCounts?.example ?? 0,
      }),
    },
  ];

  return (
    <Card variant="bordered" noPadding>
      <div className="border-b border-neutral-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-neutral-900">
          {t("projectIA.title")}
        </h3>
        <p className="mt-0.5 text-xs text-neutral-500">
          {t("projectIA.subtitle")}
        </p>
      </div>
      <ul className="divide-y divide-neutral-100">
        {rows.map((row) => (
          <li
            key={row.view}
            className="flex items-center gap-3 px-4 py-3"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
              {row.icon}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-neutral-900">
                  {row.title}
                </span>
                <span title={row.countTitle}>
                  <Badge
                    variant={row.countTitle ? "warning" : "neutral"}
                    size="sm"
                    pill
                  >
                    {row.count}
                  </Badge>
                </span>
              </div>
              <p className="truncate text-xs text-neutral-500">
                {row.description}
              </p>
            </div>
            <Button
              ghost
              size="xs"
              onClick={() => onOpen(row.view)}
              rightIcon={<ArrowRight size={12} />}
            >
              {t("projectIA.manage")}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────
// Notes manager
// ───────────────────────────────────────────────────────────

interface NotesManagerProps {
  reportKeys: string[];
  reportPills: ReportPill[];
}

function NotesManager({
  reportKeys,
  reportPills,
}: NotesManagerProps): JSX.Element {
  const { t } = useT();

  const [filterActive, setFilterActive] = useState<"true" | "false" | "all">(
    "true",
  );
  const [filterReport, setFilterReport] = useState("");
  const [search, setSearch] = useState("");

  const [notes, setNotes] = useState<AIContextNote[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AIContextNote | null>(null);
  const [regenContext, setRegenContext] = useState<{
    note: AIContextNote;
    affected: number;
  } | null>(null);
  const loadSeq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    // Only the newest request may write: typing fires one per key and
    // an older, slower answer must not overwrite a newer one.
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const items = await aiContextNotesAdminApi.list({
        active:
          filterActive === "true"
            ? true
            : filterActive === "false"
              ? false
              : null,
        report_key: filterReport || undefined,
        q: search || undefined,
        limit: NOTES_HARD_LIMIT,
      });
      if (seq !== loadSeq.current) return;
      setTruncated(items.length >= NOTES_HARD_LIMIT);
      // Scope to THIS project: only notes referencing one of its reports.
      setNotes(
        items.filter((n) =>
          n.report_keys.some((k) => reportKeys.includes(k)),
        ),
      );
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [filterActive, filterReport, search, reportKeys, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleArchive = async (note: AIContextNote): Promise<void> => {
    if (!window.confirm(t("notes.archiveConfirm"))) return;
    try {
      await aiContextNotesAdminApi.archive(note.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    }
  };

  const handleRegenPreview = async (note: AIContextNote): Promise<void> => {
    try {
      const preview = await aiContextNotesAdminApi.affectedPreview(note.id);
      setRegenContext({ note, affected: preview.affected_count });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    }
  };

  const handleSaved = async (
    saved: AIContextNote,
    affected: number,
  ): Promise<void> => {
    setCreating(false);
    setEditing(null);
    await load();
    if (affected > 0) setRegenContext({ note: saved, affected });
  };

  const activeOptions: SelectOption[] = [
    { value: "true", label: t("notes.filter.active") },
    { value: "false", label: t("notes.filter.archived") },
    { value: "all", label: t("notes.filter.all") },
  ];
  const reportOptions: SelectOption[] = [
    { value: "", label: t("notes.filter.allReports") },
    ...reportPills.map((p) => ({ value: p.key, label: p.label })),
  ];

  const fmtWindow = (start: string | null, end: string | null): string => {
    if (!start && !end) return "—";
    return `${start ?? "…"} → ${end ?? "…"}`;
  };

  const noteColumns: Column<AIContextNote>[] = [
    {
      id: "title",
      header: t("notes.col.title"),
      cell: (note) => (
        <div className="max-w-[280px]">
          <div className="truncate font-medium text-neutral-900">
            {note.title}
          </div>
          {/* Was `neutral-400`: the grey Pablo named by hand as the one
              thing NOT to copy from the reference screen. */}
          <div className="truncate text-xs text-neutral-500" title={note.body}>
            {truncateText(note.body, 80)}
          </div>
          {!note.active && (
            <span className="mt-0.5 inline-block rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
              {t("notes.archived")}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "reports",
      header: t("notes.col.reports"),
      cell: (note) => (
        <span className="block max-w-[200px] truncate font-mono text-xs text-neutral-600">
          {note.report_keys.join(", ")}
        </span>
      ),
    },
    {
      id: "slice",
      header: t("notes.col.slice"),
      cell: (note) => (
        <span className="font-mono text-xs text-neutral-600">
          {Object.keys(note.filters).length > 0
            ? Object.entries(note.filters)
                .map(([k, v]) => `${k}=${v}`)
                .join(", ")
            : "—"}
        </span>
      ),
    },
    {
      id: "window",
      header: t("notes.col.window"),
      cell: (note) => (
        <span className="whitespace-nowrap text-xs text-neutral-600">
          {fmtWindow(note.date_start, note.date_end)}
        </span>
      ),
    },
    {
      id: "visibility",
      header: t("notes.col.visibility"),
      cell: (note) =>
        note.reference_allowed ? (
          <Badge variant="neutral" size="sm" pill>
            {t("notes.visibility.mentionable")}
          </Badge>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning-dark">
            <EyeOff size={9} />
            {t("notes.visibility.internal")}
          </span>
        ),
    },
  ];

  // Three actions were three unlabelled icons in `neutral-500`; the only
  // way to learn which was which was to hover each one. The shared table
  // puts three behind a kebab, with their names.
  const noteActions: RowAction<AIContextNote>[] = [
    {
      id: "edit",
      label: t("common.edit"),
      icon: <Pencil size={14} />,
      onSelect: (note) => setEditing(note),
    },
    {
      id: "regen",
      label: t("notes.regen"),
      icon: <RotateCcw size={14} />,
      onSelect: (note) => void handleRegenPreview(note),
    },
    {
      id: "archive",
      label: t("notes.archive"),
      icon: <Archive size={14} />,
      danger: true,
      hidden: (note) => !note.active,
      onSelect: (note) => void handleArchive(note),
    },
  ];

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark">
          <AlertCircle size={14} className="shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            aria-label={t("common.close")}
          >
            <X size={12} />
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Select
          options={activeOptions}
          value={filterActive}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setFilterActive(e.target.value as "true" | "false" | "all")
          }
          aria-label={t("notes.filter.all")}
        />
        <Select
          options={reportOptions}
          value={filterReport}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setFilterReport(e.target.value)
          }
          aria-label={t("notes.filter.report")}
        />
        <Input
          type="text"
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
          placeholder={t("notes.search")}
          aria-label={t("notes.search")}
          className="min-w-[200px] flex-1"
        />
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus size={14} />}
          onClick={() => setCreating(true)}
        >
          {t("notes.new")}
        </Button>
      </div>

      {loading ? (
        <Card variant="bordered" noPadding>
          <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-neutral-500">
            <Spinner size={16} ariaLabel={t("common.loading")} />
            {t("common.loading")}
          </div>
        </Card>
      ) : (
        // The card chrome stays on the outside: DataTable draws the
        // table, not the panel, so a caller can put it in a Section
        // (the hub) or a Card (here) without the two fighting.
        <Card variant="bordered" noPadding className="p-3">
          <DataTable
            rows={notes}
            rowKey={(n) => n.id}
            caption={t("projectIA.row.notes")}
            // The endpoint caps at 500 and this pane then filters client
            // side, so past that the list is short and does not say so.
            // Front-only pass: name the cut instead of pretending.
            footnote={truncated ? t("projectIA.truncatedHint") : undefined}
            empty={
              <EmptyState
                icon={<StickyNote size={20} />}
                title={t("notes.empty")}
                action={
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<Plus size={14} />}
                    onClick={() => setCreating(true)}
                  >
                    {t("notes.new")}
                  </Button>
                }
              />
            }
            rowProps={(n) => (n.active ? {} : { className: "opacity-60" })}
            columns={noteColumns}
            actions={noteActions}
          />
        </Card>
      )}

      {(creating || editing) && (
        <NoteModal
          reportPills={reportPills}
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(saved, affected) => void handleSaved(saved, affected)}
        />
      )}

      {regenContext && (
        <RegenAffectedModal
          note={regenContext.note}
          affectedCount={regenContext.affected}
          onClose={() => setRegenContext(null)}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// Note create / edit modal
// ───────────────────────────────────────────────────────────

interface NoteFormState {
  title: string;
  body: string;
  report_keys: string[];
  filterPairs: Array<{ key: string; value: string }>;
  date_start: string;
  date_end: string;
  reference_allowed: boolean;
}

const EMPTY_NOTE_FORM: NoteFormState = {
  title: "",
  body: "",
  report_keys: [],
  filterPairs: [],
  date_start: "",
  date_end: "",
  reference_allowed: true,
};

function noteToForm(note: AIContextNote): NoteFormState {
  return {
    title: note.title,
    body: note.body,
    report_keys: [...note.report_keys],
    filterPairs: Object.entries(note.filters).map(([k, v]) => ({
      key: k,
      value: v,
    })),
    date_start: note.date_start ?? "",
    date_end: note.date_end ?? "",
    reference_allowed: note.reference_allowed,
  };
}

interface NoteModalProps {
  reportPills: ReportPill[];
  initial: AIContextNote | null;
  onClose: () => void;
  onSaved: (note: AIContextNote, affected: number) => void;
}

function NoteModal({
  reportPills,
  initial,
  onClose,
  onSaved,
}: NoteModalProps): JSX.Element {
  const { t } = useT();
  const [form, setForm] = useState<NoteFormState>(() =>
    initial ? noteToForm(initial) : EMPTY_NOTE_FORM,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof NoteFormState>(
    key: K,
    value: NoteFormState[K],
  ): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const toggleReport = (key: string): void => {
    update(
      "report_keys",
      form.report_keys.includes(key)
        ? form.report_keys.filter((k) => k !== key)
        : [...form.report_keys, key],
    );
  };

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    if (!form.title.trim()) {
      setError(t("notes.errors.titleRequired"));
      return;
    }
    if (!form.body.trim()) {
      setError(t("notes.errors.bodyRequired"));
      return;
    }
    if (form.report_keys.length === 0) {
      setError(t("notes.errors.reportRequired"));
      return;
    }
    if (
      form.date_start &&
      form.date_end &&
      form.date_start > form.date_end
    ) {
      setError(t("notes.errors.dateOrder"));
      return;
    }

    const filters: Record<string, string> = {};
    for (const pair of form.filterPairs) {
      const k = pair.key.trim();
      const v = pair.value.trim();
      if (k && v) filters[k] = v;
    }

    const payload: AIContextNoteInput = {
      title: form.title.trim(),
      body: form.body.trim(),
      report_keys: form.report_keys,
      filters,
      date_start: form.date_start || null,
      date_end: form.date_end || null,
      reference_allowed: form.reference_allowed,
    };

    setSubmitting(true);
    try {
      const saved = initial
        ? await aiContextNotesAdminApi.update(initial.id, payload)
        : await aiContextNotesAdminApi.create(payload);
      const preview = await aiContextNotesAdminApi.affectedPreview(saved.id);
      onSaved(saved, preview.affected_count);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (!submitting ? onClose() : undefined)}
      title={initial ? t("notes.modal.editTitle") : t("notes.modal.createTitle")}
      size="lg"
      footer={
        <>
          <Button
            ghost
            onClick={onClose}
            disabled={submitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={submitting}
            loading={submitting}
          >
            {initial ? t("common.save") : t("common.create")}
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3 rounded-md border border-error/40 bg-error/5 px-3 py-2 text-sm text-error-dark">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <Input
          label={t("notes.field.title")}
          value={form.title}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            update("title", e.target.value)
          }
          maxLength={200}
          autoFocus
          disabled={submitting}
          fullWidth
        />

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-neutral-700">
            {t("notes.field.body")}
          </span>
          <textarea
            value={form.body}
            onChange={(e) => update("body", e.target.value)}
            rows={5}
            disabled={submitting}
            className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-neutral-700">
            {t("notes.field.reports")}
          </span>
          <div className="flex flex-wrap gap-2">
            {reportPills.map((p) => {
              const on = form.report_keys.includes(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => toggleReport(p.key)}
                  disabled={submitting}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    on
                      ? "border-primary/30 bg-primary-soft text-primary"
                      : "border-neutral-200 bg-neutral-50 text-neutral-600 hover:bg-neutral-100"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
            {reportPills.length === 0 && (
              <span className="text-xs text-neutral-500">
                {t("adminUsers.noReports")}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-neutral-700">
              {t("notes.field.dateStart")}
            </span>
            <input
              type="date"
              value={form.date_start}
              onChange={(e) => update("date_start", e.target.value)}
              disabled={submitting}
              className="w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-neutral-700">
              {t("notes.field.dateEnd")}
            </span>
            <input
              type="date"
              value={form.date_end}
              onChange={(e) => update("date_end", e.target.value)}
              disabled={submitting}
              className="w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="block text-xs font-medium text-neutral-700">
              {t("notes.field.filters")}
            </span>
            <button
              type="button"
              onClick={() =>
                update("filterPairs", [
                  ...form.filterPairs,
                  { key: "", value: "" },
                ])
              }
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-primary hover:bg-primary-soft"
            >
              <Plus size={12} /> {t("notes.addFilter")}
            </button>
          </div>
          <div className="space-y-1.5">
            {form.filterPairs.map((pair, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={pair.key}
                  onChange={(e) =>
                    update(
                      "filterPairs",
                      form.filterPairs.map((p, i) =>
                        i === idx ? { ...p, key: e.target.value } : p,
                      ),
                    )
                  }
                  placeholder="country"
                  className="flex-1 rounded-md border border-neutral-200 px-2 py-1 font-mono text-xs"
                />
                <span className="text-neutral-500">=</span>
                <input
                  type="text"
                  value={pair.value}
                  onChange={(e) =>
                    update(
                      "filterPairs",
                      form.filterPairs.map((p, i) =>
                        i === idx ? { ...p, value: e.target.value } : p,
                      ),
                    )
                  }
                  placeholder="BR"
                  className="flex-1 rounded-md border border-neutral-200 px-2 py-1 font-mono text-xs"
                />
                <button
                  type="button"
                  onClick={() =>
                    update(
                      "filterPairs",
                      form.filterPairs.filter((_, i) => i !== idx),
                    )
                  }
                  className="rounded-md p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-600"
                  aria-label={t("common.delete")}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {form.filterPairs.length === 0 && (
              <p className="text-xs italic text-neutral-500">
                {t("notes.filtersHint")}
              </p>
            )}
          </div>
        </div>

        <label className="flex items-start gap-2 border-t border-neutral-100 pt-3">
          <input
            type="checkbox"
            checked={form.reference_allowed}
            onChange={(e) => update("reference_allowed", e.target.checked)}
            disabled={submitting}
            className="mt-0.5 rounded border-neutral-300"
          />
          <span className="text-xs text-neutral-700">
            <span className="font-medium">
              {t("notes.field.referenceAllowed")}
            </span>
            <span className="mt-0.5 block text-xs text-neutral-500">
              {t("notes.referenceAllowedHint")}
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────
// Regenerate-affected modal — preview count + live progress
// ───────────────────────────────────────────────────────────

interface RegenAffectedModalProps {
  note: AIContextNote;
  affectedCount: number;
  onClose: () => void;
}

function RegenAffectedModal({
  note,
  affectedCount,
  onClose,
}: RegenAffectedModalProps): JSX.Element {
  const { t } = useT();
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<NoteRegenStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Reset on mount: <StrictMode> (main.tsx) mounts, unmounts and
  // re-mounts in dev, and a flag set only by the cleanup would stay
  // `true` and kill polling for good.
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      stopPolling();
    };
  }, [stopPolling]);

  const poll = useCallback(
    async (id: string): Promise<void> => {
      try {
        const next = await aiContextNotesAdminApi.regenerateStatus(
          note.id,
          id,
        );
        if (unmountedRef.current) return;
        setStatus(next);
        if (next.status === "running") {
          pollingRef.current = setTimeout(
            () => void poll(id),
            REGEN_POLL_INTERVAL_MS,
          );
        }
      } catch (err) {
        if (unmountedRef.current) return;
        setError(formatAdminAiError(err));
      }
    },
    [note.id],
  );

  const handleStart = async (): Promise<void> => {
    setError(null);
    setStarting(true);
    try {
      const res = await aiContextNotesAdminApi.startRegenerate(note.id);
      if (unmountedRef.current) return;
      setJobId(res.job_id);
      void poll(res.job_id);
    } catch (err) {
      setError(formatAdminAiError(err));
    } finally {
      setStarting(false);
    }
  };

  const percent =
    status && status.total > 0
      ? Math.round((status.processed / status.total) * 100)
      : 0;
  const done = status?.status === "done";
  const failed = status?.status === "error";

  return (
    <Modal
      open
      onClose={onClose}
      title={t("notes.regen.title")}
      size="md"
      footer={
        done || failed ? (
          <Button variant="primary" onClick={onClose}>
            {t("common.close")}
          </Button>
        ) : (
          <>
            <Button ghost onClick={onClose}>
              {t("notes.regen.later")}
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleStart()}
              disabled={starting || !!jobId || affectedCount === 0}
              loading={starting || !!jobId}
            >
              {affectedCount === 0
                ? t("notes.regen.none")
                : t("notes.regen.start")}
            </Button>
          </>
        )
      }
    >
      <p className="text-sm text-neutral-700">
        {t("notes.regen.prompt", { count: affectedCount })}
      </p>
      <p className="mt-2 text-xs text-neutral-500">
        {t("notes.regen.costHint")}
      </p>

      {error && (
        <div
          role="alert"
          className="mt-3 rounded-md border border-error/40 bg-error/5 px-3 py-2 text-xs text-error-dark"
        >
          {error}
        </div>
      )}

      {status && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-neutral-600">
              {done
                ? t("notes.regen.done", { count: status.regenerated })
                : failed
                  ? t("notes.regen.failed", { error: status.error ?? "?" })
                  : `${status.processed} / ${status.total}`}
            </span>
            <span className="font-mono text-neutral-500">{percent}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-neutral-100">
            <div
              className={`h-full transition-all ${
                failed
                  ? "bg-error"
                  : done
                    ? "bg-success/100"
                    : "bg-primary"
              }`}
              style={{ width: `${Math.max(percent, failed ? 100 : 2)}%` }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────
// Shared training manager (column_docs | synonyms | example)
// ───────────────────────────────────────────────────────────

interface TrainingManagerProps {
  slug: string;
  type: ProjectTrainingType;
}

function TrainingManager({ slug, type }: TrainingManagerProps): JSX.Element {
  const { t } = useT();

  const [rows, setRows] = useState<ProjectTrainingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ProjectTrainingRow | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const all = await projectTrainingApi.list(slug);
      setRows(all.filter((r) => r.training_type === type));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [slug, type, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async (row: ProjectTrainingRow): Promise<void> => {
    if (!window.confirm(t("knowledge.deleteConfirm"))) return;
    try {
      await projectTrainingApi.remove(slug, row.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark">
          <AlertCircle size={14} className="shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            aria-label={t("common.close")}
          >
            <X size={12} />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-neutral-900">
            {t(`projectIA.row.${type}`)}
          </h3>
          <p className="text-xs text-neutral-500">
            {t(`projectIA.row.${type}Desc`)}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus size={14} />}
          onClick={() => setCreating(true)}
        >
          {t("knowledge.add")}
        </Button>
      </div>

      <Card variant="bordered" noPadding>
        {loading ? (
          <div className="flex items-center justify-center gap-2 px-6 py-10 text-sm text-neutral-500">
            <Spinner size={14} ariaLabel={t("common.loading")} />
            {t("common.loading")}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<BookOpen size={20} />}
            title={t("knowledge.empty")}
            action={
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus size={14} />}
                onClick={() => setCreating(true)}
              >
                {t("knowledge.add")}
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-neutral-100">
            {rows.map((row) => (
              <li key={row.id} className="flex items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-neutral-900">
                    {row.name || "—"}
                  </div>
                  <p
                    className="mt-0.5 truncate font-mono text-xs text-neutral-500"
                    title={row.content}
                  >
                    {truncateText(row.content, 160)}
                  </p>
                </div>
                {/* Two actions, so the shared component keeps them
                    inline — with their names, which the two bare icons
                    never had. */}
                <RowActions
                  row={row}
                  actions={[
                    {
                      id: "edit",
                      label: t("common.edit"),
                      icon: <Pencil size={14} />,
                      onSelect: (r) => setEditing(r),
                    },
                    {
                      id: "delete",
                      label: t("common.delete"),
                      icon: <Trash2 size={14} />,
                      danger: true,
                      onSelect: (r) => void handleDelete(r),
                    },
                  ]}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>

      {(creating || editing) && (
        <TrainingRowModal
          slug={slug}
          type={type}
          initial={editing}
          nextPosition={rows.length}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

interface TrainingRowModalProps {
  slug: string;
  type: ProjectTrainingType;
  initial: ProjectTrainingRow | null;
  nextPosition: number;
  onClose: () => void;
  onSaved: () => void;
}

function TrainingRowModal({
  slug,
  type,
  initial,
  nextPosition,
  onClose,
  onSaved,
}: TrainingRowModalProps): JSX.Element {
  const { t } = useT();
  const [name, setName] = useState(initial?.name ?? "");
  const [content, setContent] = useState(initial?.content ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (): Promise<void> => {
    if (!content.trim()) {
      setError(t("knowledge.contentRequired"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        training_type: type,
        name: name.trim(),
        content,
        position: initial?.position ?? nextPosition,
      };
      if (initial) {
        await projectTrainingApi.update(slug, initial.id, body);
      } else {
        await projectTrainingApi.create(slug, body);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (!saving ? onClose() : undefined)}
      title={
        initial
          ? t("knowledge.modal.editTitle")
          : t("knowledge.modal.addTitle")
      }
      size="lg"
      footer={
        <>
          <Button ghost onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={saving}
            loading={saving}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3 rounded-md border border-error/40 bg-error/5 px-3 py-2 text-sm text-error-dark">
          {error}
        </div>
      )}
      <div className="space-y-4">
        <Input
          label={t("knowledge.field.name")}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
          maxLength={128}
          autoFocus
          disabled={saving}
          fullWidth
        />
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-neutral-700">
            {t("knowledge.field.content")}
          </span>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={10}
            disabled={saving}
            className="w-full rounded-lg border border-neutral-200 px-3 py-2 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>
      </div>
    </Modal>
  );
}
